import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { api } from '../api/client';
import type { Me } from '../api/types';
import App from '../App';
import { signinState } from '../lib/auth';
import { hasClientRole } from '../lib/roles';
import SelfServiceApp from '../SelfServiceApp';
import { SessionProvider } from '../state/SessionContext';

function Splash({ label }: { label: string }) {
  return (
    <div className="splash">
      <div className="sidebar-logo splash-logo">U</div>
      <div className="splash-label">{label}</div>
    </div>
  );
}

export function AppBootstrap() {
  const auth = useAuth();
  const triedSignin = useRef(false);

  useEffect(() => {
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.error &&
      !triedSignin.current
    ) {
      triedSignin.current = true;
      void auth.signinRedirect({ state: signinState() });
    }
  }, [auth]);

  const backgroundRefreshFailed =
    auth.isAuthenticated && auth.error?.source === 'signinSilent';

  if (auth.error && !backgroundRefreshFailed) {
    return (
      <div className="splash">
        <div className="sidebar-logo splash-logo">U</div>
        <div className="splash-label">Authentication failed: {auth.error.message}</div>
        <button
          className="btn-secondary"
          onClick={() => void auth.signinRedirect({ state: signinState() })}
        >
          Try again
        </button>
      </div>
    );
  }
  if (auth.isLoading && !auth.isAuthenticated) {
    return <Splash label="Authenticating…" />;
  }
  if (!auth.isAuthenticated) {
    return <Splash label="Redirecting to login…" />;
  }
  return <AuthenticatedBootstrap />;
}

function AuthenticatedBootstrap() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reloadMe = useCallback(async () => {
    setMe(await api.me());
  }, []);

  useEffect(() => {
    reloadMe().catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : String(cause));
    });
  }, [reloadMe]);

  if (error) {
    return <Splash label={`Could not load your profile: ${error}`} />;
  }
  if (!me) {
    return <Splash label="Loading profile…" />;
  }
  return (
    <SessionProvider me={me} reloadMe={reloadMe}>
      {hasClientRole(me.clientRole) ? <App /> : <SelfServiceApp />}
    </SessionProvider>
  );
}
